import type React from "react";
import type { OpenOverlayArgs } from "./OverlayContext";
import type { OpenDialogOptions, OpenWindowOptions, OverlayHandle } from "./types";

/** Императивный API overlay, доступный вне React hooks. */
export interface ImperativeOverlayApi {
    /** Открывает surface по полному набору аргументов provider API. */
    openOverlay: (args: OpenOverlayArgs) => OverlayHandle;
    /**
     * Открывает компонент в `window`-surface.
     * @template TProps тип пропсов компонента
     */
    openWindow: <TProps>(
        component: React.ComponentType<TProps>,
        props: TProps,
        options?: OpenWindowOptions,
    ) => OverlayHandle;
    /**
     * Открывает компонент в `dialog`-surface.
     * @template TProps тип пропсов компонента
     */
    openDialog: <TProps>(
        component: React.ComponentType<TProps>,
        props: TProps,
        options?: OpenDialogOptions,
    ) => OverlayHandle;
    /** Закрывает surface по идентификатору. */
    closeOverlay: (id: string) => void;
    /** Закрывает верхний overlay; возвращает `true`, если что-то закрыто. */
    closeTopmostOverlay: () => boolean;
    /** Есть ли открытые overlay. */
    hasOpenOverlays: () => boolean;
}

let imperativeOverlayApi: ImperativeOverlayApi | null = null;

/**
 * Регистрирует текущий инстанс императивного overlay API.
 * @param api объект API или `null` для очистки ссылки
 */
export function setImperativeOverlayApi(api: ImperativeOverlayApi | null): void {
    imperativeOverlayApi = api;
}

/**
 * Возвращает текущий императивный overlay API.
 * @throws Error если `OverlayProvider` ещё не смонтирован
 */
export function getImperativeOverlayApi(): ImperativeOverlayApi {
    if (imperativeOverlayApi == null) {
        throw new Error(
            "getImperativeOverlayApi: OverlayProvider не смонтирован. Оборачивайте приложение в <OverlayProvider />.",
        );
    }

    return imperativeOverlayApi;
}
